import type { StateTree, TreeLeaf, TreeMachine } from '../components/model/state-tree';

/*
 * WHERE THE GHOST TREE GOES (Step 6 of docs/state-subgraph-plan.md), in the SVG units of
 * <vitesse-statechart>. No Lit, no i18n and no DOM: the renderer gives the size of each level-2
 * chart (it lays them out with the root's own layout), and a node test can call this.
 *
 * The tree is an org chart under the root chart. A trunk leaves the selected state and meets a bus.
 * From the bus, 1 drop goes to each column: a machine of level 1 (its box, and its statechart of
 * level 2 under the box), or a leaf (an input or an output of the root). The drop carries the
 * ports, and its arrow says the direction: down into the box for a write, up out of it for a read.
 * The columns are 1 row, centred under the state. A row that is too wide wraps, and a spine on the
 * left joins the buses of the rows.
 *
 * The trunk goes straight down from the state, or, if another root state is in the way, out of the
 * state's right side and down past the right of the root chart.
 */

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** What the tree hangs from. */
export interface TreeAnchor {
  /** The selected state's box. */
  state: Box;
  /** The lowest point of the root chart, its edges and labels included. */
  bottom: number;
  /** The right edge of the root chart's states. */
  right: number;
  /** The boxes of the other states of the root chart: a straight trunk must not cross 1 of them. */
  others: readonly Box[];
}

/** 1 line of text in a machine box. */
export interface BoxLine {
  text: string;
  kind: 'title' | 'text' | 'fault';
}

/** A drop from a bus to a column, with the ports it carries. */
export interface Drop {
  x: number;
  y1: number;
  y2: number;
  /** The ports, `present, cmd = true`; '' for a leaf, whose box names it. */
  label: string;
  /** An arrow into the box (the state writes), out of it (the state reads), or both. */
  write: boolean;
  read: boolean;
}

export interface PlacedMachine {
  machine: TreeMachine;
  box: Box;
  lines: BoxLine[];
  /** Each full path of the box, for its tooltip: the box shows the short form. */
  tooltip: string;
  drop: Drop;
  /** The level-2 chart: where its origin goes, and the line from the box to it. Absent without the template's text. */
  chart?: { dx: number; dy: number; link: { x: number; y1: number; y2: number } };
}

export interface PlacedLeaf {
  leaf: TreeLeaf;
  box: Box;
  text: string;
  tooltip: string;
  drop: Drop;
}

export interface TreeLayout {
  /** The SVG path from the selected state to the first bus. */
  trunk: string;
  buses: Array<{ y: number; x1: number; x2: number }>;
  /** The line that joins the buses of the rows, when the columns wrap. */
  spine?: { x: number; y1: number; y2: number };
  machines: PlacedMachine[];
  leaves: PlacedLeaf[];
  bounds: Box;
}

/** The widest row of columns before it wraps: near the root layout's, so the tree reads at the tile's width. */
const ROW_W = 640;
const GAP_X = 28;
/** From the root chart down to the first bus, and from a bus down to its boxes. */
const TREE_GAP = 28;
const DROP_H = 44;
/** From the bottom of a column to the bus of the next row. */
const ROW_GAP = 32;
/** From a machine's box down to its statechart. */
const LINK_H = 18;
const PAD = 10;
const TITLE_H = 18;
const LINE_H = 15;
const MIN_BOX_W = 110;
/** The widths of a character of the fonts the stylesheet gives (approximate, as textW of the renderer). */
const TITLE_CHAR = 7.6;
const TEXT_CHAR = 6.6;
const LABEL_CHAR = 6.8;
/** A line of a box is cut to this many characters. The tooltip has the whole of it. */
const MAX_CHARS = 40;

/** A path as a box shows it: the symbol of a controller, else its last 2 segments. */
export function shortPath(path: string): string {
  const symbol = /\/symbols\/(.+)$/.exec(path);
  if (symbol) return symbol[1];
  const parts = path.split('/');
  return parts.length > 2 ? parts.slice(-2).join('/') : path;
}

/** The y of the middle of line `i` of a machine's box: the title, then the smaller lines. */
export function lineY(box: Box, i: number): number {
  return i === 0 ? box.y + PAD + TITLE_H / 2 : box.y + PAD + TITLE_H + (i - 1) * LINE_H + LINE_H / 2;
}

const cut = (text: string): string => (text.length > MAX_CHARS ? `${text.slice(0, MAX_CHARS - 1)}…` : text);
const widthOf = (line: BoxLine): number => line.text.length * (line.kind === 'title' ? TITLE_CHAR : TEXT_CHAR);

/** The ports of a drop, as its label shows them. */
function portsLabel(m: TreeMachine): string {
  return m.ports.map((p) => (p.value !== undefined ? `${p.port} = ${p.value}` : p.port)).join(', ');
}

function linesOf(m: TreeMachine): { lines: BoxLine[]; tooltip: string } {
  const sub = m.array ? `${m.template} ×${m.array.count}` : m.template;
  const plc = m.plc.map((p) => `${p.port} ${p.direction === 'in' ? '←' : '→'} `);
  const lines: BoxLine[] = [
    { text: cut(m.name), kind: 'title' },
    { text: cut(sub), kind: 'text' },
    ...m.plc.map((p, i): BoxLine => ({ text: cut(`${plc[i]}${shortPath(p.path)}`), kind: 'text' })),
  ];
  if (m.fault) lines.push({ text: cut(`⚠ ${m.fault}`), kind: 'fault' });
  if (m.array && m.array.faulted > 0) lines.push({ text: `⚠ ${m.array.faulted} / ${m.array.count}`, kind: 'fault' });
  const tooltip = [m.name, sub, ...m.plc.map((p, i) => `${plc[i]}${p.path}`), ...(m.fault ? [m.fault] : [])].join('\n');
  return { lines, tooltip };
}

function leafText(l: TreeLeaf): { text: string; tooltip: string } {
  const name = l.direction === 'write' && l.value !== undefined ? `${l.name} = ${l.value}` : l.name;
  const arrow = l.direction === 'read' ? '←' : '→';
  return { text: cut(l.path ? `${name} ${arrow} ${shortPath(l.path)}` : name), tooltip: l.path ? `${name} ${arrow} ${l.path}` : name };
}

/** 1 column of the tree before it is placed: its width, and its height under the bus. */
interface Column {
  w: number;
  h: number;
  place: (x: number, busY: number) => void;
}

/**
 * The layout of the tree under the anchor. `chartOf` gives the bounds of a template's chart as the
 * renderer lays it out (its x and y can be below 0, for edge labels), or null for no chart.
 */
export function layoutTree(tree: StateTree, anchor: TreeAnchor, chartOf: (scxml: string) => Box | null): TreeLayout {
  const machines: PlacedMachine[] = [];
  const leaves: PlacedLeaf[] = [];
  const columns: Column[] = [];

  for (const m of tree.machines) {
    const { lines, tooltip } = linesOf(m);
    const label = portsLabel(m);
    const boxW = Math.max(MIN_BOX_W, ...lines.map(widthOf)) + PAD * 2;
    const boxH = PAD * 2 + TITLE_H + (lines.length - 1) * LINE_H;
    const chart = m.chart.scxml ? chartOf(m.chart.scxml) : null;
    const w = Math.max(boxW, chart?.w ?? 0, label.length * LABEL_CHAR + 12);
    const h = DROP_H + boxH + (chart ? LINK_H + chart.h : 0);
    const read = m.ports.some((p) => p.direction === 'read');
    const write = m.ports.some((p) => p.direction === 'write');
    columns.push({
      w,
      h,
      place: (x, busY) => {
        const cx = x + w / 2;
        const box = { x: cx - boxW / 2, y: busY + DROP_H, w: boxW, h: boxH };
        const placed: PlacedMachine = { machine: m, box, lines, tooltip, drop: { x: cx, y1: busY, y2: box.y, label, read, write } };
        if (chart) {
          const top = box.y + boxH + LINK_H;
          placed.chart = { dx: cx - chart.w / 2 - chart.x, dy: top - chart.y, link: { x: cx, y1: box.y + boxH, y2: top } };
        }
        machines.push(placed);
      },
    });
  }
  for (const l of tree.leaves) {
    const { text, tooltip } = leafText(l);
    const boxW = Math.max(60, text.length * TEXT_CHAR) + PAD * 2;
    const boxH = LINE_H + PAD * 1.4;
    columns.push({
      w: boxW,
      h: DROP_H + boxH,
      place: (x, busY) => {
        const box = { x, y: busY + DROP_H, w: boxW, h: boxH };
        const drop = { x: x + boxW / 2, y1: busY, y2: box.y, label: '', read: l.direction === 'read', write: l.direction === 'write' };
        leaves.push({ leaf: l, box, text, tooltip, drop });
      },
    });
  }

  // The rows: as many columns as fit in ROW_W, at least 1.
  const rows: Column[][] = [];
  for (const c of columns) {
    const row = rows[rows.length - 1];
    const width = row ? row.reduce((s, k) => s + k.w, 0) + row.length * GAP_X + c.w : 0;
    if (!row || width > ROW_W) rows.push([c]);
    else row.push(c);
  }

  const s = anchor.state;
  const cx = s.x + s.w / 2;
  const bottom = s.y + s.h;
  const firstBus = Math.max(anchor.bottom, bottom) + TREE_GAP;
  // A straight trunk crosses a state that starts below the selected one and before the bus.
  const blocked = anchor.others.some((o) => o.x < cx + 4 && o.x + o.w > cx - 4 && o.y >= bottom && o.y < firstBus);
  let trunkX = cx;
  let trunk = `M ${cx} ${bottom} L ${cx} ${firstBus}`;
  if (blocked) {
    const midY = s.y + s.h / 2;
    trunkX = Math.max(anchor.right, s.x + s.w) + 16;
    trunk = `M ${s.x + s.w} ${midY} L ${trunkX - 8} ${midY} Q ${trunkX} ${midY} ${trunkX} ${midY + 8} L ${trunkX} ${firstBus}`;
  }

  const buses: TreeLayout['buses'] = [];
  const lefts: number[] = [];
  let busY = firstBus;
  for (const row of rows) {
    const rowW = row.reduce((sum, c) => sum + c.w, 0) + (row.length - 1) * GAP_X;
    let x = cx - rowW / 2;
    lefts.push(x);
    const drops: number[] = [];
    for (const c of row) {
      c.place(x, busY);
      drops.push(x + c.w / 2);
      x += c.w + GAP_X;
    }
    buses.push({ y: busY, x1: Math.min(...drops), x2: Math.max(...drops) });
    busY += Math.max(...row.map((c) => c.h)) + ROW_GAP;
  }
  // The first bus reaches the trunk. With more rows, a spine left of every row joins each bus.
  if (buses[0]) {
    buses[0].x1 = Math.min(buses[0].x1, trunkX);
    buses[0].x2 = Math.max(buses[0].x2, trunkX);
  }
  let spine: TreeLayout['spine'];
  if (buses.length > 1) {
    const x = Math.min(...lefts) - 14;
    spine = { x, y1: buses[0].y, y2: buses[buses.length - 1].y };
    for (const b of buses) b.x1 = Math.min(b.x1, x);
  }

  // The bounds: every box, chart, label and line.
  const xs: number[] = [cx, trunkX, ...buses.flatMap((b) => [b.x1, b.x2])];
  const ys: number[] = [bottom, firstBus];
  if (spine) xs.push(spine.x);
  for (const p of machines) {
    xs.push(
      p.box.x,
      p.box.x + p.box.w,
      p.drop.x - (p.drop.label.length * LABEL_CHAR) / 2 - 6,
      p.drop.x + (p.drop.label.length * LABEL_CHAR) / 2 + 6,
    );
    ys.push(p.box.y + p.box.h);
    if (p.chart && p.machine.chart.scxml) {
      const size = chartOf(p.machine.chart.scxml);
      if (size) {
        xs.push(p.chart.dx + size.x, p.chart.dx + size.x + size.w);
        ys.push(p.chart.dy + size.y + size.h);
      }
    }
  }
  for (const l of leaves) {
    xs.push(l.box.x, l.box.x + l.box.w);
    ys.push(l.box.y + l.box.h);
  }
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    trunk,
    buses,
    ...(spine ? { spine } : {}),
    machines,
    leaves,
    bounds: { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y },
  };
}
